import React from 'react';
import { TabsContent } from '../ui/tabs';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Banknote, CheckCircle, Send } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';

export function DisbursementsTab({
  isOwner, disbursements, disbursementSummary,
  onRecordDisbursement,
}) {
  return (
    <TabsContent value="disbursements">
      <div className="space-y-4">
        {/* Investor banner for received payments (investor view) */}
        {!isOwner && disbursements.length > 0 && (
          <Card className="rounded-sm border-emerald-200 bg-emerald-50" data-testid="disbursement-investor-banner">
            <CardContent className="py-4">
              <div className="flex items-center gap-2 mb-2">
                <Banknote className="w-5 h-5 text-emerald-600" />
                <h3 className="font-semibold text-emerald-800">Payments Received</h3>
              </div>
              <div className="space-y-2">
                {disbursements.map(d => (
                  <div key={d.id} className="flex items-center justify-between bg-white p-3 rounded-sm border border-emerald-100">
                    <div>
                      <p className="font-medium text-emerald-800">{formatCurrency(d.amount)}</p>
                      <p className="text-xs text-muted-foreground">via {d.payment_method} on {new Date(d.payment_date).toLocaleDateString()}</p>
                    </div>
                    <Badge className="rounded-sm text-xs bg-emerald-100 text-emerald-700 border-emerald-200">
                      <Send className="w-3 h-3 mr-1" /> Sent
                    </Badge>
                  </div>
                ))}
              </div>
              {disbursementSummary.length > 0 && (
                <div className="mt-3 pt-3 border-t border-emerald-200 flex items-center justify-between text-sm">
                  <span className="text-emerald-700">Total Received</span>
                  <span className="font-bold text-emerald-800">{formatCurrency(disbursementSummary[0]?.total_paid || 0)}</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Owner view - Disbursement summary per investor */}
        {isOwner && (
          <>
            <Card className="rounded-sm border" data-testid="disbursement-summary-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold uppercase tracking-wide">Investor Disbursements</CardTitle>
                  <Button
                    size="sm"
                    className="bg-forest-500 hover:bg-forest-600 text-white rounded-sm text-xs"
                    onClick={onRecordDisbursement}
                    data-testid="record-disbursement-btn"
                  >
                    <Banknote className="w-3 h-3 mr-1" /> Record Disbursement
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {disbursementSummary.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No JV investors to disburse to.</p>
                ) : (
                  <div className="space-y-3">
                    {disbursementSummary.map(s => (
                      <div key={s.investor_name} className="p-4 border rounded-sm" data-testid={`disbursement-investor-${s.investor_name.replace(/\s/g, '-')}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="font-medium">{s.investor_name}</p>
                            {s.investor_email && <p className="text-xs text-muted-foreground">{s.investor_email}</p>}
                          </div>
                          {s.fully_disbursed ? (
                            <Badge className="rounded-sm text-xs bg-emerald-100 text-emerald-700 border-emerald-200">
                              <CheckCircle className="w-3 h-3 mr-1" /> Fully Disbursed
                            </Badge>
                          ) : (
                            <Badge className="rounded-sm text-xs bg-amber-100 text-amber-700 border-amber-200">
                              Pending
                            </Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                          <div>
                            <p className="text-muted-foreground text-xs">Capital</p>
                            <p className="font-medium">{formatCurrency(s.capital_contributed)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-xs">Profit</p>
                            <p className="font-medium text-emerald-600">{formatCurrency(s.investor_profit)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-xs">Total Owed</p>
                            <p className="font-bold">{formatCurrency(s.total_owed)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-xs">Remaining</p>
                            <p className={`font-bold ${s.remaining <= 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                              {formatCurrency(s.remaining)}
                            </p>
                          </div>
                        </div>
                        {s.disbursement_count > 0 && (
                          <div className="mt-2 pt-2 border-t">
                            <p className="text-xs text-muted-foreground">{s.disbursement_count} payment{s.disbursement_count !== 1 ? 's' : ''} sent totaling {formatCurrency(s.total_paid)}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Disbursement History */}
            {disbursements.length > 0 && (
              <Card className="rounded-sm border" data-testid="disbursement-history-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold uppercase tracking-wide">Payment History</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 text-xs uppercase text-muted-foreground">Investor</th>
                          <th className="text-left py-2 text-xs uppercase text-muted-foreground">Amount</th>
                          <th className="text-left py-2 text-xs uppercase text-muted-foreground">Method</th>
                          <th className="text-left py-2 text-xs uppercase text-muted-foreground">Date</th>
                          <th className="text-left py-2 text-xs uppercase text-muted-foreground">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {disbursements.map(d => (
                          <tr key={d.id} className="border-b last:border-0">
                            <td className="py-2 font-medium">{d.investor_name}</td>
                            <td className="py-2 text-emerald-600 font-medium">{formatCurrency(d.amount)}</td>
                            <td className="py-2">
                              <Badge variant="outline" className="rounded-sm text-[10px]">{d.payment_method}</Badge>
                            </td>
                            <td className="py-2">{new Date(d.payment_date).toLocaleDateString()}</td>
                            <td className="py-2 text-muted-foreground text-xs">{d.notes || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </TabsContent>
  );
}
